import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { 
  Wallet, TrendingUp, TrendingDown, Receipt, FileText, 
  AlertTriangle, Euro, PieChart, BarChart3, CreditCard, FileDown, Settings, ScanLine, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import GuidedTour, { TOUR_CONFIGS } from '../../components/GuidedTour';

// Sub-components
import ExerciceSelector from '../../components/Comptabilite/ExerciceSelector';
import BudgetSection from '../../components/Comptabilite/BudgetSection';
import FacturesSection from '../../components/Comptabilite/FacturesSection';
import AppelsFondsSection from '../../components/Comptabilite/AppelsFondsSection';
import ImpayesSection from '../../components/Comptabilite/ImpayesSection';
import ConfigSection from '../../components/Comptabilite/ConfigSection';
import BanqueSection from '../../components/Comptabilite/BanqueSection';
import ReportsSection from '../../components/Comptabilite/ReportsSection';
import OCRSection from '../../components/Comptabilite/OCRSection';
import GrandLivreSection from '../../components/Comptabilite/GrandLivreSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComptabiliteDashboard() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [exercices, setExercices] = useState([]);
  const [selectedExercice, setSelectedExercice] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'tableau-bord');
  const [stats, setStats] = useState(null);

  // Handle URL tab parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Fetch buildings
  const fetchBuildings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/buildings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
        if (data.length > 0 && !selectedBuilding) {
          setSelectedBuilding(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [token, selectedBuilding]);

  // Fetch exercices for selected building
  const fetchExercices = useCallback(async () => {
    if (!selectedBuilding) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/comptabilite/exercices?building_id=${selectedBuilding.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setExercices(data);
        
        // Select current exercice (en_cours) or most recent
        const current = data.find(e => e.statut === 'en_cours') || data[0];
        if (current) {
          setSelectedExercice(current);
        }
      }
    } catch (error) {
      console.error('Error fetching exercices:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedBuilding, token]);

  // Fetch stats for dashboard
  const fetchStats = useCallback(async () => {
    if (!selectedExercice) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/comptabilite/exercices/${selectedExercice.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [selectedExercice, token]);

  useEffect(() => {
    fetchBuildings();
  }, [fetchBuildings]);

  useEffect(() => {
    fetchExercices();
  }, [fetchExercices]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const tabs = [
    { id: 'tableau-bord', label: 'Tableau de bord', icon: PieChart },
    { id: 'budget', label: 'Budget', icon: BarChart3 },
    { id: 'factures', label: 'Factures', icon: Receipt },
    { id: 'ocr-factures', label: 'OCR Factures', icon: ScanLine },
    { id: 'appels-fonds', label: 'Appels de fonds', icon: FileText },
    { id: 'grand-livre', label: 'Grand Livre', icon: BookOpen },
    { id: 'banque', label: 'Banque', icon: CreditCard },
    { id: 'impayes', label: 'Impayés & Relances', icon: AlertTriangle },
    { id: 'rapports', label: 'Rapports', icon: FileDown },
    { id: 'config', label: 'Configuration', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="comptabilite-dashboard">
      {/* Guided Tour */}
      <GuidedTour {...TOUR_CONFIGS.comptabilite} />

      {/* Header - Style unifié avec Carnet d'entretien */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#00D3A8]/10 rounded-xl">
            <Wallet className="h-8 w-8 text-[#00D3A8]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Comptabilité</h1>
            <p className="text-gray-500">Gérez les finances de vos copropriétés</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3" data-tour="exercice-selector">
          {/* Building Selector */}
          <select
            value={selectedBuilding?.id || ''}
            onChange={(e) => {
              const building = buildings.find(b => b.id === e.target.value);
              setSelectedBuilding(building);
            }}
            className="px-4 py-2 border border-gray-200 rounded-xl bg-white min-w-[200px]"
            data-testid="building-selector"
          >
            {buildings.map(b => (
              <option key={b.id} value={b.id}>{b.nom || b.adresse}</option>
            ))}
          </select>
          
          {/* Exercice Selector */}
          <ExerciceSelector
            exercices={exercices}
            selectedExercice={selectedExercice}
            onSelect={setSelectedExercice}
            buildingId={selectedBuilding?.id}
            token={token}
            onExerciceCreated={fetchExercices}
          />
        </div>
      </div>

      {/* Tabs - Style unifié avec coins arrondis en haut */}
      <div className="border-b border-gray-200 mb-6" data-tour="tabs-navigation">
        <div className="flex flex-wrap gap-1">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all rounded-t-lg ${
                  isActive
                    ? 'bg-[#00D3A8] text-white'
                    : 'text-gray-600 hover:bg-gray-100 bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {activeTab === 'tableau-bord' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-5 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Budget prévisionnel</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatMoney(stats?.budget_previsionnel)}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl">
                  <PieChart className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Recettes</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {formatMoney(stats?.total_recettes)}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Dépenses</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">
                    {formatMoney(stats?.total_depenses)}
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-xl">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Solde</p>
                  <p className={`text-2xl font-bold mt-1 ${(stats?.solde || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatMoney(stats?.solde)}
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${(stats?.solde || 0) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <Euro className={`h-6 w-6 ${(stats?.solde || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions - Style amélioré */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Actions rapides</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button
                onClick={() => setActiveTab('factures')}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-[#00D3A8]/5 hover:border-[#00D3A8] border border-transparent transition-all group"
              >
                <div className="p-2 bg-white rounded-lg group-hover:bg-[#00D3A8]/10">
                  <Receipt className="h-6 w-6 text-[#00D3A8]" />
                </div>
                <span className="text-sm font-medium text-gray-700">Nouvelle facture</span>
              </button>
              <button
                onClick={() => setActiveTab('appels-fonds')}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-[#00D3A8]/5 hover:border-[#00D3A8] border border-transparent transition-all group"
              >
                <div className="p-2 bg-white rounded-lg group-hover:bg-[#00D3A8]/10">
                  <FileText className="h-6 w-6 text-[#00D3A8]" />
                </div>
                <span className="text-sm font-medium text-gray-700">Appel de fonds</span>
              </button>
              <button
                onClick={() => setActiveTab('impayes')}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-orange-50 hover:border-orange-300 border border-transparent transition-all group"
              >
                <div className="p-2 bg-white rounded-lg group-hover:bg-orange-100">
                  <AlertTriangle className="h-6 w-6 text-orange-500" />
                </div>
                <span className="text-sm font-medium text-gray-700">Voir impayés</span>
              </button>
              <button
                onClick={() => setActiveTab('budget')}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-blue-50 hover:border-blue-300 border border-transparent transition-all group"
              >
                <div className="p-2 bg-white rounded-lg group-hover:bg-blue-100">
                  <BarChart3 className="h-6 w-6 text-blue-500" />
                </div>
                <span className="text-sm font-medium text-gray-700">Budget</span>
              </button>
            </div>
          </div>

          {/* Exercice Info - Amélioré */}
          {selectedExercice && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Exercice en cours</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  selectedExercice.statut === 'en_cours' ? 'bg-green-100 text-green-700' :
                  selectedExercice.statut === 'cloture' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {selectedExercice.statut === 'en_cours' ? 'En cours' :
                   selectedExercice.statut === 'cloture' ? 'Clôturé' :
                   selectedExercice.statut === 'approuve' ? 'Approuvé' : selectedExercice.statut}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 text-xs mb-1">Nom</p>
                  <p className="font-medium text-gray-900">{selectedExercice.nom}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 text-xs mb-1">Début</p>
                  <p className="font-medium text-gray-900">{new Date(selectedExercice.date_debut).toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 text-xs mb-1">Fin</p>
                  <p className="font-medium text-gray-900">{new Date(selectedExercice.date_fin).toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 text-xs mb-1">Report antérieur</p>
                  <p className="font-medium text-gray-900">{formatMoney(selectedExercice.report_anterieur)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Info légale */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-blue-900">Comptabilité de copropriété</p>
                <p className="text-sm text-blue-700 mt-1">
                  La comptabilité doit être tenue conformément au décret n°2005-240 du 14 mars 2005. 
                  Les comptes doivent être présentés en assemblée générale annuelle.
                </p>
              </div>
            </div>
          </div>

          {/* Guide pas à pas - Comptabilité de copropriété */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-tour="guide-compta">
            <div className="bg-gradient-to-r from-[#00D3A8] to-[#00B892] p-5">
              <h3 className="font-bold text-white text-lg">Guide : Mettre en place l'exercice comptable</h3>
              <p className="text-white/80 text-sm mt-1">
                Suivez ces étapes pour une gestion comptable conforme au décret n°2005-240
              </p>
            </div>
            
            <div className="p-5 space-y-4">
              {/* Étape 1 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  1
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">Créer l'exercice comptable</h4>
                    <button 
                      onClick={() => document.querySelector('[data-testid="exercice-selector-btn"]')?.click()}
                      className="text-xs text-[#00D3A8] hover:underline font-medium"
                    >
                      Créer →
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Définissez la période (généralement 12 mois, du 1er janvier au 31 décembre). 
                    L'exercice doit correspondre à la période entre deux assemblées générales.
                  </p>
                  <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-xs text-amber-700">
                      <strong>Obligation légale :</strong> L'exercice comptable doit être approuvé en AG (art. 14-1 loi du 10 juillet 1965).
                    </p>
                  </div>
                </div>
              </div>

              {/* Étape 2 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  2
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">Établir le budget prévisionnel</h4>
                    <button 
                      onClick={() => setActiveTab('budget')}
                      className="text-xs text-[#00D3A8] hover:underline font-medium"
                    >
                      Budget →
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Le budget prévisionnel doit être voté en assemblée générale. Il comprend les charges courantes 
                    (entretien, assurance, honoraires syndic, électricité parties communes...).
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-700">Charges courantes</p>
                      <p className="text-xs text-gray-500">Entretien, assurance, énergie</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-700">Charges exceptionnelles</p>
                      <p className="text-xs text-gray-500">Travaux votés en AG</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Étape 3 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  3
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">Configurer les clés de répartition (tantièmes)</h4>
                    <button 
                      onClick={() => setActiveTab('config')}
                      className="text-xs text-[#00D3A8] hover:underline font-medium"
                    >
                      Configuration →
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Les charges sont réparties selon les tantièmes définis dans le règlement de copropriété. 
                    Vérifiez que chaque lot a bien ses quotes-parts (charges générales, ascenseur, chauffage...).
                  </p>
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-700">
                      <strong>Important :</strong> Les tantièmes sont fixés par le règlement de copropriété et ne peuvent être modifiés que par décision unanime de l'AG.
                    </p>
                  </div>
                </div>
              </div>

              {/* Étape 4 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  4
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">Émettre les appels de fonds</h4>
                    <button 
                      onClick={() => setActiveTab('appels-fonds')}
                      className="text-xs text-[#00D3A8] hover:underline font-medium"
                    >
                      Appels de fonds →
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Les provisions pour charges courantes sont appelées trimestriellement (1/4 du budget). 
                    Chaque coproprietaire reçoit un appel de fonds selon ses tantièmes.
                  </p>
                  <div className="mt-2 flex gap-2">
                    <span className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">T1 : 1er janvier</span>
                    <span className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">T2 : 1er avril</span>
                    <span className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">T3 : 1er juillet</span>
                    <span className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">T4 : 1er octobre</span>
                  </div>
                </div>
              </div>

              {/* Étape 5 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  5
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">Enregistrer les factures et dépenses</h4>
                    <button 
                      onClick={() => setActiveTab('factures')}
                      className="text-xs text-[#00D3A8] hover:underline font-medium"
                    >
                      Factures →
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Enregistrez toutes les factures fournisseurs en les affectant aux bonnes catégories de charges. 
                    Conservez les justificatifs pendant 10 ans minimum.
                  </p>
                </div>
              </div>

              {/* Étape 6 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  6
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">Suivre les impayés et relances</h4>
                    <button 
                      onClick={() => setActiveTab('impayes')}
                      className="text-xs text-[#00D3A8] hover:underline font-medium"
                    >
                      Impayés →
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Identifiez les coproprietaires en retard de paiement et envoyez des relances. 
                    Après mise en demeure, le syndic peut engager une procédure de recouvrement.
                  </p>
                  <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-100">
                    <p className="text-xs text-red-700">
                      <strong>Attention :</strong> Les charges impayées peuvent entraîner des pénalités de retard et des frais de recouvrement à la charge du coproprietaire défaillant.
                    </p>
                  </div>
                </div>
              </div>

              {/* Étape 7 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  7
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">Rapprochement bancaire</h4>
                    <button 
                      onClick={() => setActiveTab('banque')}
                      className="text-xs text-[#00D3A8] hover:underline font-medium"
                    >
                      Banque →
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Vérifiez régulièrement que le solde comptable correspond au solde bancaire. 
                    Identifiez et régularisez les écarts (opérations en attente, erreurs...).
                  </p>
                </div>
              </div>

              {/* Étape 8 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  8
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">Clôturer l'exercice et préparer l'AG</h4>
                    <button 
                      onClick={() => setActiveTab('rapports')}
                      className="text-xs text-[#00D3A8] hover:underline font-medium"
                    >
                      Rapports →
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    En fin d'exercice, générez les 5 annexes comptables obligatoires pour l'assemblée générale 
                    et calculez la régularisation des charges pour chaque coproprietaire.
                  </p>
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-700 mb-2">Annexes obligatoires (décret 2005-240) :</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• <strong>Annexe 1 :</strong> État financier (bilan)</li>
                      <li>• <strong>Annexe 2 :</strong> Compte de gestion général</li>
                      <li>• <strong>Annexe 3 :</strong> Compte de gestion pour opérations courantes</li>
                      <li>• <strong>Annexe 4 :</strong> Compte de gestion pour travaux et opérations exceptionnelles</li>
                      <li>• <strong>Annexe 5 :</strong> État des travaux et opérations exceptionnelles</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer du guide */}
            <div className="bg-gray-50 p-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Besoin d'aide ? Consultez notre documentation complète ou contactez le support.
                </p>
                <button 
                  onClick={() => window.open('/aide', '_blank')}
                  className="text-xs text-[#00D3A8] hover:underline font-medium"
                >
                  Accéder à l'aide
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'budget' && selectedExercice && (
        <BudgetSection
          buildingId={selectedBuilding?.id}
          exerciceId={selectedExercice?.id}
          token={token}
        />
      )}

      {activeTab === 'factures' && selectedExercice && (
        <FacturesSection
          buildingId={selectedBuilding?.id}
          exerciceId={selectedExercice?.id}
          token={token}
        />
      )}

      {activeTab === 'ocr-factures' && selectedBuilding && (
        <OCRSection
          buildingId={selectedBuilding?.id}
          buildingName={selectedBuilding?.nom || selectedBuilding?.adresse}
          token={token}
        />
      )}

      {activeTab === 'appels-fonds' && selectedExercice && (
        <div data-tour="appels-section">
          <AppelsFondsSection
            buildingId={selectedBuilding?.id}
            exerciceId={selectedExercice?.id}
            token={token}
          />
        </div>
      )}

      {activeTab === 'impayes' && selectedBuilding && (
        <ImpayesSection
          buildingId={selectedBuilding?.id}
          token={token}
        />
      )}

      {activeTab === 'banque' && selectedBuilding && (
        <BanqueSection
          buildingId={selectedBuilding?.id}
          token={token}
        />
      )}

      {activeTab === 'rapports' && selectedBuilding && (
        <ReportsSection
          buildingId={selectedBuilding?.id}
          exerciceId={selectedExercice?.id}
          token={token}
        />
      )}

      {activeTab === 'grand-livre' && selectedBuilding && (
        <GrandLivreSection
          buildingId={selectedBuilding?.id}
          exercice={selectedExercice}
          token={token}
        />
      )}

      {activeTab === 'config' && selectedBuilding && (
        <ConfigSection
          buildingId={selectedBuilding?.id}
          token={token}
        />
      )}
    </div>
  );
}
